'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';
import { SetOfTheDayCard } from './set-of-the-day-card';
import type { ProductWithCategories } from '@/lib/api/products';

export interface SetOfTheDayPairProps {
  products: [ProductWithCategories, ProductWithCategories];
  onClick?: (product: ProductWithCategories) => void;
  className?: string;
}

// Автоскролл carousel'а на мобиле раз в 6 секунд.
const AUTO_INTERVAL_MS = 6000;
// После ручного свайпа автоскролл паузится на 10 секунд, чтобы пользователь
// успел рассмотреть выбранный слайд.
const PAUSE_AFTER_INTERACTION_MS = 10000;
// Минимальное смещение по X для распознавания свайпа (px).
const SWIPE_THRESHOLD_PX = 50;

const SetOfTheDayPairInner: React.FC<SetOfTheDayPairProps> = ({ products, onClick, className }) => {
  return (
    <div className={className}>
      {/* ПК: два баннера бок о бок, каждый в compact-режиме */}
      <div className="hidden md:grid md:grid-cols-2 md:gap-4 lg:gap-5">
        {products.map((p) => (
          <SetOfTheDayCard key={p.id} product={p} onClick={onClick} compact />
        ))}
      </div>

      {/* Мобиль: carousel с автоскроллом, свайпом и точками */}
      <MobileCarousel products={products} onClick={onClick} />
    </div>
  );
};

interface MobileCarouselProps {
  products: ProductWithCategories[];
  onClick?: (product: ProductWithCategories) => void;
}

const MobileCarousel: React.FC<MobileCarouselProps> = ({ products, onClick }) => {
  const count = products.length;
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [isPaused, setIsPaused] = React.useState(false);
  const pauseTimerRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const touchStartXRef = React.useRef<number | null>(null);

  // Автоскролл — переключает слайд раз в AUTO_INTERVAL_MS.
  // Пауза при isPaused (после ручного свайпа или клика по точке).
  React.useEffect(() => {
    if (isPaused || count < 2) return;
    const id = window.setInterval(() => {
      setActiveIndex((i) => (i + 1) % count);
    }, AUTO_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [isPaused, count]);

  // Чистим таймер паузы при размонтировании (предотвращает setState на unmounted).
  React.useEffect(() => {
    return () => {
      if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
    };
  }, []);

  const pauseTemporarily = React.useCallback(() => {
    setIsPaused(true);
    if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
    pauseTimerRef.current = setTimeout(() => setIsPaused(false), PAUSE_AFTER_INTERACTION_MS);
  }, []);

  const goTo = React.useCallback(
    (idx: number) => {
      setActiveIndex(((idx % count) + count) % count);
      pauseTemporarily();
    },
    [count, pauseTemporarily]
  );

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0]?.clientX ?? null;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const startX = touchStartXRef.current;
    if (startX === null) return;
    const endX = e.changedTouches[0]?.clientX ?? startX;
    const delta = endX - startX;
    touchStartXRef.current = null;
    if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return;
    if (delta > 0) {
      goTo(activeIndex - 1);
    } else {
      goTo(activeIndex + 1);
    }
  };

  return (
    <div className="md:hidden">
      <div className="overflow-hidden">
        <div
          className="flex transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${activeIndex * 100}%)` }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          role="region"
          aria-roledescription="carousel"
          aria-label="Сеты дня"
        >
          {products.map((p, i) => (
            <div
              key={p.id}
              className="w-full flex-shrink-0"
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} из ${count}`}
              aria-hidden={i !== activeIndex}
            >
              <SetOfTheDayCard product={p} onClick={onClick} />
            </div>
          ))}
        </div>
      </div>

      {/* Точки-индикаторы */}
      <div className="flex justify-center gap-2 mt-3">
        {products.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`Перейти к слайду ${i + 1}`}
            aria-current={i === activeIndex}
            className={cn(
              // Точка-индикатор круглая и остаётся круглой: это pill, а не
              // hairline (та роль — для тонкой ПОЛОСЫ прогресса, не для точки).
              'h-2 rounded-pill transition-all duration-200',
              i === activeIndex
                ? 'w-6 bg-[var(--primary)]'
                : 'w-2 bg-[var(--border-default)] hover:bg-[var(--text-tertiary)]'
            )}
          />
        ))}
      </div>
    </div>
  );
};

export const SetOfTheDayPair = React.memo(SetOfTheDayPairInner);
SetOfTheDayPair.displayName = 'SetOfTheDayPair';
